import { useState } from 'react';
import { Project } from '../types/project';
import { Settings, Check } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { useAuth } from '../contexts/AuthContext';
import { isValidEmail } from '../validation/projectCompletionValidation';
import { isProfileEmpty, profileFieldsFromProject } from '../lib/preparerDefaults';
import { US_STATES, normalizeStateCode } from '../lib/usStates';
import { preparerComplete, preparerSummaryLines } from '../lib/preparerSummary';

interface ProjectInfoFormProps {
  project: Partial<Project>;
  onChange: (updates: Partial<Project>) => void;
  isReadOnly?: boolean;
  showRequiredHighlights?: boolean;
  /** Opens Account Settings on the Company Profile tab. */
  onEditProfile?: () => void;
}

export function ProjectInfoForm({ project, onChange, isReadOnly = false, showRequiredHighlights = false, onEditProfile }: ProjectInfoFormProps) {
  const [phoneError, setPhoneError] = useState('');
  const [customerEmailError, setCustomerEmailError] = useState('');
  const [preparerEmailError, setPreparerEmailError] = useState('');
  const { companyProfile, updateCompanyProfile } = useAuth();
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState('');

  // Preparer Information fills from the profile, so on every job after the
  // first it reads as a summary with Edit (context/ux.md item 6). It opens
  // itself while the name or email is blank, and stays open once the person
  // has started typing in it — a block must never fold under a cursor.
  const complete = preparerComplete(project);
  const [manualOpen, setManualOpen] = useState<boolean | null>(null);
  const preparerExpanded = manualOpen ?? !complete;

  // Offered only to someone who has never set a profile up, so accepting it
  // retires the offer for good and a partly-filled profile is never nagged.
  // Gated on a valid address so it cannot bank a typo as the new default.
  const offerProfileSave =
    !isReadOnly &&
    !profileSaved &&
    isProfileEmpty(companyProfile) &&
    isValidEmail(project.preparer_email || '');

  async function handleSaveToProfile() {
    setSavingProfile(true);
    setProfileSaveError('');
    const { error } = await updateCompanyProfile(profileFieldsFromProject(project));
    setSavingProfile(false);
    if (error) setProfileSaveError(error.message);
    else setProfileSaved(true);
  }

  function reqClass(value: string | undefined) {
    return showRequiredHighlights && !value?.trim()
      ? 'w-full px-3 py-2 border border-red-500 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
      : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';
  }

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return (
    <div className="space-y-6">
      {/* One list, shared by the customer and company state inputs below. The
          inputs stay free text: the list suggests, it does not restrict. */}
      <datalist id="us-state-codes">
        {US_STATES.map((s) => (
          <option key={s.code} value={s.code}>{s.name}</option>
        ))}
      </datalist>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Customer Information <Tooltip text="Customer name and address should match utility account information. Pull from the customer's electric bill." />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name <span className="text-red-600">*</span> {showRequiredHighlights && !project.customer_name?.trim() && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <input
              type="text"
              value={project.customer_name || ''}
              onChange={(e) => onChange({ customer_name: e.target.value })}
              data-testid="customer-name"
              className={reqClass(project.customer_name)}
              disabled={isReadOnly}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address <span className="text-red-600">*</span> {showRequiredHighlights && !project.customer_address?.trim() && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <input
              type="text"
              value={project.customer_address || ''}
              onChange={(e) => onChange({ customer_address: e.target.value })}
              data-testid="customer-address"
              className={reqClass(project.customer_address)}
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City/Town
            </label>
            <input
              type="text"
              value={project.customer_city || ''}
              onChange={(e) => onChange({ customer_city: e.target.value })}
              data-testid="customer-city"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <input
              type="text"
              list="us-state-codes"
              value={project.customer_state || ''}
              onChange={(e) => onChange({ customer_state: normalizeStateCode(e.target.value) })}
              data-testid="customer-state"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
              placeholder="Two-letter code"
              maxLength={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code <span className="text-red-600">*</span> {showRequiredHighlights && !project.customer_zip?.trim() && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <input
              type="text"
              value={project.customer_zip || ''}
              onChange={(e) => onChange({ customer_zip: e.target.value })}
              data-testid="customer-zip"
              className={reqClass(project.customer_zip)}
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={project.customer_email || ''}
              data-testid="customer-email"
              onChange={(e) => {
                onChange({ customer_email: e.target.value });
                if (customerEmailError && isValidEmail(e.target.value)) setCustomerEmailError('');
              }}
              onBlur={() => {
                const val = project.customer_email || '';
                if (val && !isValidEmail(val)) setCustomerEmailError('Please enter a valid email address.');
                else setCustomerEmailError('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
            {customerEmailError && <p className="text-xs text-red-600 mt-1">{customerEmailError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={project.customer_phone || ''}
              data-testid="customer-phone"
              onChange={(e) => {
                const formatted = formatPhone(e.target.value);
                onChange({ customer_phone: formatted });
                if (phoneError && formatted.replace(/\D/g, '').length === 10) setPhoneError('');
              }}
              onBlur={() => {
                const digits = (project.customer_phone || '').replace(/\D/g, '');
                if (digits.length > 0 && digits.length !== 10) setPhoneError('Please enter a valid 10-digit phone number.');
                else setPhoneError('');
              }}
              placeholder="(xxx) xxx-xxxx"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
            {phoneError && <p className="text-xs text-red-600 mt-1">{phoneError}</p>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Preparer Information
          </h2>
          <div className="flex items-center gap-2">
            {complete && (
              <button
                type="button"
                onClick={() => setManualOpen(!preparerExpanded)}
                data-testid="btn-toggle-preparer"
                aria-expanded={preparerExpanded}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {preparerExpanded ? 'Done' : 'Edit'}
              </button>
            )}
            {onEditProfile && !isReadOnly && (
              <>
                <button
                  type="button"
                  onClick={onEditProfile}
                  data-testid="btn-edit-profile-settings"
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                  title="Open Account Settings → Company Profile"
                >
                  <Settings className="w-4 h-4" />
                  Edit Profile Settings
                </button>
                <Tooltip text="These fields fill automatically from your Company Profile, and the report date defaults to the date the project was created. Everything here stays editable — changes apply to this project only. To change the values every new project starts with, click Edit Profile Settings to open Account Settings → Company Profile." />
              </>
            )}
          </div>
        </div>

        {!preparerExpanded && (
          <div data-testid="preparer-summary" className="space-y-0.5 text-sm">
            {preparerSummaryLines(project).map((line, i) => (
              <p key={i} className={i === 0 ? 'font-medium text-gray-900' : 'text-gray-600'}>{line}</p>
            ))}
          </div>
        )}

        {preparerExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" onInput={() => { if (manualOpen !== true) setManualOpen(true); }}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prepared By <span className="text-red-600">*</span> {showRequiredHighlights && !project.prepared_by?.trim() && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <input
              type="text"
              value={project.prepared_by || ''}
              onChange={(e) => onChange({ prepared_by: e.target.value })}
              data-testid="prepared-by"
              className={reqClass(project.prepared_by)}
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={project.preparer_title || ''}
              onChange={(e) => onChange({ preparer_title: e.target.value })}
              data-testid="preparer-title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Number
            </label>
            <input
              type="text"
              value={project.preparer_license || ''}
              onChange={(e) => onChange({ preparer_license: e.target.value })}
              data-testid="preparer-license"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preparer Email <span className="text-red-600">*</span> {showRequiredHighlights && !project.preparer_email?.trim() && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <input
              type="email"
              value={project.preparer_email || ''}
              data-testid="preparer-email"
              onChange={(e) => {
                onChange({ preparer_email: e.target.value });
                if (preparerEmailError && isValidEmail(e.target.value)) setPreparerEmailError('');
              }}
              onBlur={() => {
                const val = project.preparer_email || '';
                if (val && !isValidEmail(val)) setPreparerEmailError('Please enter a valid email address.');
                else setPreparerEmailError('');
              }}
              placeholder="your@email.com"
              className={reqClass(project.preparer_email)}
              disabled={isReadOnly}
            />
            {preparerEmailError && <p className="text-xs text-red-600 mt-1">{preparerEmailError}</p>}
            <p className="text-xs text-gray-500 mt-1">
              Used as reply-to address on AMI authorization requests
            </p>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Date <Tooltip text="Defaults to the date this project was created. Change it to the date you want printed on the report — for example, the day you submit to the AHJ." />
            </label>
            {isReadOnly ? (
              <input
                type="text"
                value={project.report_date
                  ? new Date(project.report_date + 'T00:00:00').toLocaleDateString()
                  : ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed text-gray-500"
              />
            ) : (
              <>
                <input
                  type="date"
                  value={project.report_date || ''}
                  onChange={(e) => onChange({ report_date: e.target.value })}
                  data-testid="report-date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ colorScheme: 'light' }}
                />
                <p className="text-xs text-gray-400 mt-1">mm/dd/yyyy</p>
              </>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={project.company_name || ''}
              onChange={(e) => onChange({ company_name: e.target.value })}
              data-testid="company-name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Address
            </label>
            <input
              type="text"
              value={project.company_address || ''}
              onChange={(e) => onChange({ company_address: e.target.value })}
              data-testid="company-address"
              placeholder="Street address"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company City
            </label>
            <input
              type="text"
              value={project.company_city || ''}
              onChange={(e) => onChange({ company_city: e.target.value })}
              data-testid="company-city"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                list="us-state-codes"
                value={project.company_state || ''}
                onChange={(e) => onChange({ company_state: normalizeStateCode(e.target.value) })}
                data-testid="company-state"
                placeholder="Two-letter code"
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isReadOnly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                type="text"
                value={project.company_zip || ''}
                onChange={(e) => onChange({ company_zip: e.target.value })}
                data-testid="company-zip"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isReadOnly}
              />
            </div>
          </div>
        </div>
        )}

        {offerProfileSave && (
          <div
            data-testid="offer-save-profile"
            className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3"
          >
            <p className="flex-1 min-w-[16rem] text-sm text-blue-900">
              Save these details to your profile so future projects fill in automatically.
            </p>
            <button
              type="button"
              onClick={handleSaveToProfile}
              disabled={savingProfile}
              data-testid="btn-save-to-profile"
              className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              {savingProfile ? 'Saving…' : 'Save'}
            </button>
            {profileSaveError && (
              <p className="w-full text-xs text-red-600">{profileSaveError}</p>
            )}
          </div>
        )}

        {profileSaved && (
          <div
            data-testid="profile-saved-confirmation"
            className="mt-4 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
          >
            <Check className="w-4 h-4 flex-shrink-0" />
            Saved to your profile. New projects will fill these in automatically.
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Authority Having Jurisdiction
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AHJ Jurisdiction <Tooltip text="The permitting and inspection authority for this project. Most often the town or city — for example, 'City of Burlington' or 'Town of Shelburne'." />
            </label>
            <input
              type="text"
              value={project.ahj_jurisdiction || ''}
              onChange={(e) => onChange({ ahj_jurisdiction: e.target.value })}
              data-testid="ahj-jurisdiction"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permit/Application Number <Tooltip text="Leave blank if not yet assigned. Can be added later before printing the report." />
            </label>
            <input
              type="text"
              value={project.permit_number || ''}
              onChange={(e) => onChange({ permit_number: e.target.value })}
              data-testid="permit-number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
